type ProcessChain = {
  tech: string;
  status: string;
  [key: string]: any;
};

type ProcessChains = Record<string, ProcessChain>;

type ChoiceObject = {
  value: string | number;
  text: string;
  [key: string]: any;
};

type ChoiceObjects = Record<string, ChoiceObject>;

type FeasibilityObject = Record<string, Record<string, { percent_violation: number }>>;

type NotificationOptions = {
  closeOnClick: boolean;
  displayCloseButton: boolean;
  positionClass: string;
  showDuration: number;
  theme: string;
};

type NotificationData = {
  title: string;
  message: string;
};

type TransformedObject = {
  label: string;
  value: string | number;
};

type ErrorResponse = {
  response: {
    data: Blob | { error_message: string };
  };
};

export function floatFormat(value: number | null | undefined, digits: number): string {
  if (value || value === 0) {
    return parseFloat(value.toString()).toFixed(digits);
  } else {
    return '0.00';
  }
}

export function getExtension(filename: string | null | undefined): string {
  if (filename) {
    const parts = filename.split('.');
    return parts[parts.length - 1];
  } else {
    console.log('Undefined filename in getExtension in helpers.');
    return '';
  }
}

export function isCAD(filename: string): boolean {
  const ext = getExtension(filename);
  switch (ext.toLowerCase()) {
    case 'stl':
    case 'stp':
    case 'step':
    case 'obj':
    case '3mf':
    case 'ply':
      return true;
    default:
      return false;
  }
}

export function isExcel(filename: string): boolean {
  const ext = getExtension(filename);
  switch (ext.toLowerCase()) {
    case 'xlsx':
      return true;
    default:
      return false;
  }
}

export function getProcessChain(chains: ProcessChains, techname: string): ProcessChain | undefined {
  const processChainName = Object.keys(chains).find(key => chains[key].tech === techname);
  return processChainName ? chains[processChainName] : undefined;
}

export function getProcessChainById(chains: ProcessChains, chain_id: string): ProcessChain | undefined {
  return chains[chain_id];
}

export function getChoicebyUid(objects: ChoiceObjects, uid: string | number): string | undefined {
  const key = Object.keys(objects).find(key => objects[key].value === uid);
  return key ? objects[key]?.text : undefined;
}

export function getValueOfNestedObject(obj: Record<string, any>, key: string): any {
  return key.split('.').reduce((value, el) => value[el], obj);
}

export function compareValues(val1: string | number, val2: string | number): number {
  if (typeof val1 === 'number' && typeof val2 === 'number') {
    return val1 - val2;
  } else if (typeof val1 === 'number') {
    return -1;
  } else if (typeof val2 === 'number') {
    return 1;
  } else {
    return val1.toString().toLowerCase().localeCompare(val2.toString().toLowerCase());
  }
}

export function sortNumbersAsc(a: number | null, b: number | null): number {
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return a - b;
}

export function sortNumbersDesc(a: number | null, b: number | null): number {
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return b - a;
}

export function sortStringDesc(a: string, b: string): number {
  if (a < b) {
    return 1;
  }
  if (a > b) {
    return -1;
  }
  return 0;
}

export function totalFeasFromObj(feasObj: FeasibilityObject): boolean | null {
  const listOfFeasChecks: number[] = [];
  Object.values(feasObj).forEach(singleFeas => {
    Object.values(singleFeas).forEach(minMaxEntry => {
      listOfFeasChecks.push(minMaxEntry.percent_violation);
    });
  });

  return totalFeasFromValues(...listOfFeasChecks);
}

export function totalFeasFromValues(...values: (number | null | undefined)[]): boolean | null {
  let totalFeas: boolean | null = null;
  for (const value of values) {
    if (value !== undefined && value !== null) {
      if (value <= warning_thresh_percent) {
        totalFeas = true;
      } else {
        totalFeas = false;
        break;
      }
    }
  }
  return totalFeas;
}

export const warning_thresh_percent = 2;

export function copyToClipboard(text: string): void {
  // Deprecated API (for old browser support)
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);

  // New API
  navigator.clipboard.writeText(text);
}

export function capitalizeFirstLetter(string: string): string {
  if (typeof string !== 'string' || string.length === 0) {
    return string;
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getDayAndMonthFromDate(startDate: string | Date, endDate: string | Date, format: number = 2): string {
  const startPeriod = new Date(startDate);
  const endPeriod = new Date(endDate);

  const startDay = startPeriod.getUTCDate().toString().padStart(2, '0');
  const startMonth = (startPeriod.getUTCMonth() + 1).toString().padStart(2, '0');
  const endDay = endPeriod.getUTCDate().toString().padStart(2, '0');
  const endMonth = (endPeriod.getUTCMonth() + 1).toString().padStart(2, '0');

  const dayAndMonth = `${startDay}.${startMonth} - ${endDay}.${endMonth}`;
  const daYMonthAndYear = `${startDay}.${startMonth}.${startPeriod.getUTCFullYear()} - ${endDay}.${endMonth}.${endPeriod.getUTCFullYear()}`;

  return format === 3 ? daYMonthAndYear : dayAndMonth;
}

export function checkAllProcessChainStatus(chains: ProcessChains, status: string): boolean {
  return checkProcessChainStatus(chains, status, allElementsTrue);
}

export function checkAnyProcessChainStatus(chains: ProcessChains, status: string): boolean {
  return checkProcessChainStatus(chains, status, anyElementsTrue);
}

function checkProcessChainStatus(
  chains: ProcessChains,
  status: string,
  checkFunc: (statusArray: boolean[]) => boolean
): boolean {
  const statusArray: boolean[] = [];
  const keys = Object.keys(chains);
  if (keys.length === undefined || keys.length === 0) return false;
  keys.forEach(key => {
    statusArray.push(chains[key].status === status);
  });
  return checkFunc(statusArray);
}

function allElementsTrue(statusArray: boolean[]): boolean {
  return statusArray.every(Boolean);
}

function anyElementsTrue(statusArray: boolean[]): boolean {
  return statusArray.some(Boolean);
}

declare global {
  interface Window {
    createNotification: (options: NotificationOptions) => (data: NotificationData) => void;
  }
}

export function notify(theme: string, title: string, message: string, duration: number = 3000): void {
  window.createNotification({
    closeOnClick: true,
    displayCloseButton: false,
    positionClass: 'nfc-bottom-right',
    showDuration: duration,
    theme: theme,
  })({
    title: title,
    message: message,
  });
}

export function notifyError(messageHeader: string, error: ErrorResponse): void {
  if (error.response.data instanceof Blob) {
    handleFileResponse(error).then(error_message => {
      notify('error', messageHeader, error_message);
    });
  } else if (error.response.data instanceof Object) {
    const error_message = (error.response.data as { error_message: string }).error_message;
    notify('error', messageHeader, error_message);
  }
}

async function handleFileResponse(error: ErrorResponse): Promise<string> {
  if (error.response.data instanceof Blob) {
    const text = await error.response.data.text();
    const errorData = JSON.parse(text);
    return errorData.error_message;
  } else {
    return '';
  }
}

export function transformObjectToArray(inputObject: ChoiceObjects): TransformedObject[] {
  const result: TransformedObject[] = [];
  for (const key in inputObject) {
    if (Object.prototype.hasOwnProperty.call(inputObject, key)) {
      const item = inputObject[key];
      result.push({
        label: item.text,
        value: item.value,
      });
    }
  }

  return result;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
